import { useRef, useState, useEffect } from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {changeActiveModalNode} from "../reducers/node-modal";
import {
    changeFormPreview,
    changeIsAdvancedMode,
    changeIsPublishPageState,
    changePreview,
    setIsShowPreloader,
    setPublishEventContext,
    setNodesIdsWithWrongAction,
    toggleShowHiddenBlocks
} from "../reducers/app";
import {deleteNode, setNodes} from "../reducers/nodes";
import {useMutation} from "@apollo/client";
import {DELETE_PAGE, DUPLICATE_PAGE} from "../apollo/Mutations";
import {NodeType} from "../types/PageNode";
import {RequestPageType} from "../types/RequestPageType";
import { setCheckLinksModal, setConstructorHitsIndex, setConstructorHitsRun, setDuplicatedToast, setDuplicateModal, setFirstHitsIndex, setFirstHitsRun, setPublishLimitModal, setPublishSubpagesLimitModal, setShowGlobalFileManager, setShowUpgradeModal } from '../reducers/modalSlice';
import { useCheckLocation } from '../hooks/useCheckLocation';
import { useSubPageService } from './SubPage/SubPageService';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { checkDefaultLinks } from './NodeService';
import { LANGUAGES } from '../data/constants';
import { pageService, usePageService } from './Page/usePageService';
import { PublishLimitModalType, usePublishSubpagesLimit } from './Page/usePublishSubpagesLimit';
import { CONSTRUCTOR_EVENTS, gtagEvent } from '../utils/analytics';
import { prependFontFaceStyles } from '../fonts/FontUsageCollector';

export const useDropdown = () => {
    const dropdownRef = useRef();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dispatch = useDispatch();


    const handleClickOutside = (e) => {
        if (isDropdownOpen && dropdownRef.current && !dropdownRef.current.contains(e.target)) {
            setIsDropdownOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isDropdownOpen]);

    const handleDropdownToggle = () => {
        if (!isDropdownOpen) {
            dispatch(changeActiveModalNode(null));
        }
        setIsDropdownOpen(!isDropdownOpen);
    };

    return { dropdownRef, isDropdownOpen, handleDropdownToggle };
};

export const useScale = () => {
    const [zoom, setZoom] = useState(() => Math.round(window.devicePixelRatio * 100));

    useEffect(() => {
        const handleResize = () => {
            setZoom(Math.round(window.devicePixelRatio * 100));
        };
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    return { zoom };
};

export const useClickHandlers = (dispatch) => {
    const { i18n } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const [duplicatePage] = useMutation(DUPLICATE_PAGE);
    const pageId = useSelector((state) => state.appStorage.pageId)
    const nodes = useSelector((state) => state.nodes.present.value);
    const { page } = usePageService()
    const { selectSubPage } = useSubPageService(page)
    const { getPublishLimitModal } = usePublishSubpagesLimit()

    const changeLanguage = (newLng) => {
      const queryParams = new URLSearchParams(location.search).toString();
      const pathSegments = location.pathname.split("/").filter(Boolean);
    
      const firstSegment = pathSegments[0]?.toLowerCase();
      const currentLangInUrl = LANGUAGES.some(l => l.locale.toLowerCase() === firstSegment)
        ? firstSegment
        : "en";

      const pathWithoutLang = currentLangInUrl === "en"
        ? pathSegments.join("/")
        : pathSegments.slice(1).join("/");

      const newPath = newLng === "en"
        ? `/${pathWithoutLang}`
        : `/${newLng}/${pathWithoutLang}`;

      const finalPath = queryParams ? `${newPath}?${queryParams}` : newPath;

      i18n.changeLanguage(newLng);
      navigate(finalPath, { replace: true });
    };

    const handlerClickExport = () => {
        dispatch(changePreview(true));

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const mainBlock = document.getElementById("mainBlock");
                if (mainBlock !== null) {
                    const sourceWindow = mainBlock.ownerDocument.defaultView || window;
                    const exportedMarkup = prependFontFaceStyles(
                        mainBlock.innerHTML,
                        mainBlock,
                        sourceWindow,
                    );

                    navigator.clipboard.writeText(exportedMarkup).then(() => {
                        dispatch(changePreview(false));
                        alert("Copied to clipboard!");
                    });
                } else {
                    dispatch(changePreview(false));
                    alert("Nothing to copy! Create something");
                }
            });
        });
    };

    const tryPublishPage = async () => {
        const broadcastPublish = (state) => {
            const detail = { isPublishProccess: state };
            window.dispatchEvent(new CustomEvent('publishEvent', { detail }));
            
            const iframe = document.getElementById("mobileView");
            if (iframe && iframe.contentWindow) {
                iframe.contentWindow.dispatchEvent(new CustomEvent('publishEvent', { detail }));
            }
        };
    
        broadcastPublish(true);
    
        const iframe = document.getElementById("mobileView");
        const blockContent = document.getElementById("blockContent1");
        
        if (iframe) {
            const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
            const mobileBlockContent = iframeDocument.getElementById("blockContent1");
        
            if (mobileBlockContent !== null) {
                dispatch(changePreview(true));
                dispatch(changeIsPublishPageState(true));
            }
        } else if (blockContent !== null) {
            dispatch(changePreview(true));
            dispatch(changeIsPublishPageState(true));
        } else {
            alert("Nothing to copy!");
            broadcastPublish(false);
            return;
        }
    
        await new Promise(resolve => setTimeout(resolve, 100));
    
        broadcastPublish(false);
    }

    const handlerClickPublish = async (sourceBlock) => {
        const pageData = pageService.getPage()

        const limitModal = window.self === window.top
            ? await getPublishLimitModal(pageData)
            : (pageData?.canPublish ? null : PublishLimitModalType.PublishLimit)
        if(limitModal === PublishLimitModalType.PublishLimit){
            dispatch(setPublishLimitModal(true))
            return;
        }
        if(limitModal === PublishLimitModalType.PublishSubpagesLimit){
            dispatch(setPublishSubpagesLimitModal(true))
            return;
        }
        dispatch(setPublishEventContext({ source_block: sourceBlock, method: "button" }));
        const nodesIds = await checkDefaultLinks(nodes[0])
        dispatch(setNodesIdsWithWrongAction(nodesIds))

        if(nodesIds?.length){
            dispatch(setCheckLinksModal(true))
        }else{
            await tryPublishPage()
        }
    };

    const makePageCopy = async () => {
        try{
            const { data } = await duplicatePage({variables: {
                id: parseInt(pageId),
                isMeQr: window.self === window.top ? false : true
            }})

            if(data?.duplicatePage){
                gtagEvent(CONSTRUCTOR_EVENTS.DUPLICATE_PAGE);
                dispatch(setDuplicatedToast(true))
                if(page?.rootPage){
                    pageService.refetch(pageId)
                } else {
                    pageService.refetch(pageId)
                    selectSubPage(data?.duplicatePage?.id, data?.duplicatePage?.title)
                }
            }
        }catch(error){
            console.error(error)
            if(window.self === window.top){
                dispatch(setShowUpgradeModal(true))
            }
        }
    }

    const handleClickDuplicate = async () => {
        if(page?.subPages.length > 0){
            dispatch(setDuplicateModal(true))
            return;
        }
        await makePageCopy()
    }

    const handleClickFileManager = (isShow) => {
        dispatch(setShowGlobalFileManager(isShow))
    }

    return { handlerClickExport, handlerClickPublish, handleClickDuplicate, makePageCopy, changeLanguage, tryPublishPage, handleClickFileManager };
};

export const useAdditionalHandlers = (dispatch, previewState, nodes, parentPageId) => {
    const [deletePage] = useMutation(DELETE_PAGE);
    const pageId = useSelector((state) => state.appStorage.pageId)
    const { page } = usePageService()
    const { pageRootId, pageRootTitle, selectSubPage, removeSubpageFromAllHeaders } = useSubPageService(page)
    const type = useSelector(state => state.appStorage.type)
    const {isFormLocation} = useCheckLocation();

    const handlePlayClick = () => {
        if (previewState) {
            dispatch(changePreview(false));
            if(isFormLocation) dispatch(changeFormPreview(true))
        } else {
            gtagEvent(CONSTRUCTOR_EVENTS.PREVIEW_PAGE);
            dispatch(setIsShowPreloader(true))
            setTimeout(() => {
                dispatch(setIsShowPreloader(false))
            }, 3000)
            dispatch(changeActiveModalNode(null));
            dispatch(changePreview(true));
            if(isFormLocation) dispatch(changeFormPreview(true));
        }
    };

    const changeIsAdvancedModeAction = (checked) => {
        if (checked) {
            gtagEvent(CONSTRUCTOR_EVENTS.ENABLE_ADVANCED_MODE);
        }
        dispatch(changeIsAdvancedMode(checked));
    };

    const confirmActionAndRedirect = async () => {
        try{
            const deletedPagePublicUrl = page?.publicUrl;
            const { data } = await deletePage({variables: {id: parseInt(pageId)}})

            if(data?.deletePage){
                if(!page?.rootPage){
                    if (deletedPagePublicUrl) {
                        await removeSubpageFromAllHeaders(parseInt(pageId), deletedPagePublicUrl);
                    }
                    pageService.refetch(pageRootId)
                    selectSubPage(pageRootId, pageRootTitle)
                    return;
                }
                if (window.self !== window.top) {
                  window.top.location.href = process.env.REACT_APP_NEW_ADMIN + "/projects/";
                } else {
                  window.location.href = process.env.REACT_APP_NEW_ADMIN + "/projects/";
                }
            }
        }catch (error){
            console.error(error)
        }
    };

    const handleDelete = () => {
        let newRootNode = {
            breadCrumbTitle: "Window",
            type: NodeType.PageNode,
            id: '1',
            parentId: null,
            tagName: 'div',
            className: null,
            nodes: [],
            classes: []
        }
        if (type === RequestPageType.MobilePage) {
            newRootNode.customStyle = {'max-width': '440px', 'margin': '0px auto'}
        }
        dispatch(deleteNode({ removeNode: { ...nodes[0], nodes: [] } }));
        dispatch(setNodes({nodes: [newRootNode], persist: true}))
    };

    const handleShowHiddenBlocksClick = () => {
        dispatch(toggleShowHiddenBlocks());
    };


    return { handlePlayClick, changeIsAdvancedModeAction, confirmActionAndRedirect, handleDelete, handleShowHiddenBlocksClick, tryStartTour };


};

export const simulateClickOnChat = () => {
   const iframe = document.querySelector('iframe[name="helpcrunch-iframe"]');
   if (!iframe) {
     return;
   }
   
   window.HelpCrunch('openChat');
 }

export const tryStartTour = (hasContent, dispatch) => {
    if(hasContent){
        localStorage.setItem("activeFirstHits", JSON.stringify(false))
        dispatch(setFirstHitsRun({ run: true }));
        dispatch(setFirstHitsIndex({ index: 0 }));
    }else{
        localStorage.setItem("activeConstructorHits", JSON.stringify(false))
        dispatch(setConstructorHitsRun({ run: true }));
        dispatch(setConstructorHitsIndex({ index: 0 }));
    }
}
